import _ from "lodash";
/**--external-- */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import {
  IconButton,
  Button,
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";

import {
  ChevronLeftOutlined,
  ShareCurveOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";

/**--internal-- */
import { useI18n } from "Hooks";
import DialogueBoxV2 from "UIComponents/DialogueBoxV2";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { GRAPH_TYPE_OPTIONS } from "Worksheet/modules/WorksheetConstants";
import { updateIsQuestionTheaterModeEnable } from "Worksheet/modules/WorksheetModules";

import { getStyleStrippedText } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import QuestionDetailsSideBar from "Worksheet/components/QuestionDetailsSideBar";
import { getUserType } from "modules/CommonSelectors";
import { getGraphTypeOptions } from "Worksheet/modules/WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

/**--relative-- */
import classes from "./Header.scss";
import { styles } from "./HeaderStyles";
import { getGraphQuestionData } from "../GraphUtils";

const Header = props => {
  const { t } = useI18n(["common", "worksheet"]);

  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(false);

  const {
    updateGraphType,
    handleSaveGraph,
    type,
    handleIsGraphEnable,
    config,
    value,
    testId,
    updateIsQuestionTheaterModeEnable,
    redoState,
    undoState,
    onUndo,
    onRedo,
    userType,
    worksheetMode,
    isFromInternalTool,
    isQueriesV2Enabled,
  } = props;
  const {
    showGraphDropdown,
    enableViewQuestion,
    showSaveButton,
    isSubQuestion,
    resourceId,
    questionId,
    questionIndex,
    subQuestionParentId,
  } = config;

  useEffect(() => {
    return () => updateIsQuestionTheaterModeEnable(false);
  }, [updateIsQuestionTheaterModeEnable]);

  const graphTypeOption = getGraphTypeOptions(t);

  const disableGraph = () => {
    const key = _.get(value, "type") ? _.get(value, "type") : "classic";
    if (showGraphDropdown & showSaveButton)
      updateGraphType(_.find(graphTypeOption, { key: key }));
    handleIsGraphEnable(false);
  };

  const handleBack = () => {
    !showSaveButton ? disableGraph() : setShowDiscardChangesModal(true);
  };

  const handleSaveBtnClick = async () => {
    setLoading(true);
    await handleSaveGraph();
    setLoading(false);
  };

  // Get question data using utility function
  const { questionTitle } = getGraphQuestionData({
    questionId,
    isSubQuestion,
    subQuestionParentId,
    resourceId,
    userType,
    worksheetMode,
    isFromInternalTool,
    isQueriesV2Enabled,
    t,
  });

  const questionTitleStyle = classNames({
    [classes.questionTitle]: true,
    "heading-6": true,
  });

  const viewQuestionBtnStyle = classNames({
    [classes.viewQuestionBtn]: true,
    "heading-6": true,
  });

  const currentGraphType = _.get(
    _.find(GRAPH_TYPE_OPTIONS, { key: type }),
    "label",
    ""
  );
  return (
    <React.Fragment>
      {enableViewQuestion ? (
        <QuestionDetailsSideBar
          viewQuestion={viewQuestion}
          setViewQuestion={setViewQuestion}
          questionIndex={questionIndex}
          questionId={questionId}
          isSubQuestion={isSubQuestion}
          subQuestionParentId={subQuestionParentId}
          resourceId={resourceId}
          t={t}
          localeLabel={"worksheet:graphing"}
          userType={userType}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
      ) : null}
      <div className={classes.header}>
        <div className={classes.headingContainer}>
          <IconButton
            variant="neutral"
            type="plain"
            dsVersion="2.0"
            _legacy_variant={"plain"}
            icon={<ChevronLeftOutlined />}
            onClick={handleBack}
            testId={`${testId}-graph-handleBack`}
          />
          {showGraphDropdown ? (
            <div className={classes.graphDrop}>
              <div className="heading-5">{t("worksheet:graphing_tool")}</div>
              <div className={classes.line}></div>
              <Dropdown
                overlayClassName={classes.dropdownOverlay}
                overlay={
                  <DropdownMenu
                    options={graphTypeOption}
                    onClick={updateGraphType}
                    testId={`${testId}-graph-updateGraphType`}
                  />
                }
              >
                <ButtonDisclosure
                  dsVersion="2.0"
                  _legacy_variant="inline"
                  variant="neutral"
                  type="plain"
                  size="large"
                  _legacy_size="medium"
                >
                  <div className={classes.dropdownLabel}>
                    <span className="text-subtitle-2">
                      {t("worksheet:graph_type")}
                    </span>
                    <span className="heading-6">{t(currentGraphType)}</span>
                  </div>
                </ButtonDisclosure>
              </Dropdown>
            </div>
          ) : null}
          {enableViewQuestion ? (
            <div className={classes.questionContainer}>
              <div className={classes.questionTitleContainer}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: getStyleStrippedText({
                      text: questionTitle,
                      type: "SANITIZE_DANGER_HTML_TITLE",
                    }),
                  }}
                  className={questionTitleStyle}
                ></div>
              </div>
              <div
                className={viewQuestionBtnStyle}
                onClick={() => setViewQuestion(prev => !prev)}
                onKeyDown={e =>
                  onEnterOrSpaceKeyDown(e, () => setViewQuestion(prev => !prev))
                }
                role="button"
                tabIndex={0}
                data-test-id={`${testId}-graph-setViewQuestion`}
              >
                {t("common:view_with_label_lowercase", {
                  label: t("common:question"),
                })}
              </div>
            </div>
          ) : null}
        </div>
        {showSaveButton ? (
          <div className={classes.buttonContainer}>
            <IconButton
              variant="neutral"
              dsVersion="2.0"
              type="plain"
              _legacy_variant={"subtle"}
              icon={<ShareCurveOutlined />}
              onClick={onUndo}
              testId={`${testId}-graph-undo`}
              disabled={_.isEqual(undoState, 0)}
              className={classes.mirror}
            />{" "}
            <IconButton
              variant="neutral"
              type="plain"
              dsVersion="2.0"
              _legacy_variant={"subtle"}
              icon={<ShareCurveOutlined />}
              onClick={onRedo}
              testId={`${testId}-graph-redo`}
              disabled={_.isEqual(redoState, 0)}
            />
            <Button
              dsVersion="2.0"
              variant="progressive"
              size="large"
              _legacy_variant="progressive"
              _legacy_size="large"
              onClick={handleSaveBtnClick}
              icon={<TickCircleFilled />}
              testId={`${testId}-graph-onSaveClick`}
            >
              {t("common:save")}
            </Button>
          </div>
        ) : null}
      </div>
      {showDiscardChangesModal ? (
        <DialogueBoxV2
          modalTitle={t("common:you_have_unsaved_changes")}
          showModal
          onClickButton1={() => {
            setShowDiscardChangesModal(false);
          }}
          onClickButton2={() => {
            setShowDiscardChangesModal(false);
            disableGraph();
          }}
          toggleDialogueBoxDisplay={() => {
            setShowDiscardChangesModal(false);
          }}
          modalBody={t("common:all_changes_will_be_discarded")}
          button1={t("common:cancel")}
          button2={t("common:discard_changes")}
          size="auto"
          closeButtonSize="medium"
          customModalContentClass={classes.modalContent}
          button1Props={{
            size: "large",
            variant: "outlined-subtle",
          }}
          button2Props={{
            size: "large",
          }}
          headerStyles={styles.headerStyles}
          modalBodyStyle={styles.modalBodyStyle}
          footerContainerStyle={styles.footerContainerStyle}
          testId={`${testId}-graph-toggleModal`}
        />
      ) : null}

      {loading ? <FullScreenLoader /> : null}
    </React.Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { isFromInternalTool } = ownProps;

  const userType = getUserType(state);

  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(
    false,
    isFromInternalTool
  );
  return { userType, isQueriesV2Enabled };
};

const mapActionCreators = {
  updateIsQuestionTheaterModeEnable,
};

export default connect(mapStateToProps, mapActionCreators)(Header);
